import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('sending');
    setFormError('');

    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        if (typeof value === 'string') params.append(key, value);
      }
      formData.append('access_key', '72bad765-7b02-461d-b0be-863f5e10770e');
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed as Record<string, string>).message
          || responseText;
        setFormError(serverMsg || t('footer_error'));
        setFormStatus('error');
      }
    } catch {
      setFormError(t('footer_error'));
      setFormStatus('error');
    }
  };

  return (
    <footer className="bg-background-100 pt-16 md:pt-20 pb-8 px-4 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12 mb-12">
          <div className="lg:col-span-1">
            <h3 className="font-heading text-2xl font-semibold text-foreground-950 mb-3">{t('footer_brand')}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4 whitespace-pre-line">
              {t('footer_address')}
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: 'ri-instagram-line', href: '#' },
                { icon: 'ri-facebook-line', href: '#' },
                { icon: 'ri-twitter-x-line', href: '#' },
              ].map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-white transition-all duration-300 cursor-pointer"
                  aria-label={social.icon.replace('ri-', '').replace('-line', '')}
                  rel="nofollow"
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground-950 uppercase tracking-wider mb-4">{t('footer_quick_links')}</h4>
            <div className="space-y-3">
              {[
                { label: t('footer_availability'), href: '#search' },
                { label: t('footer_amenities'), href: '#amenities' },
                { label: 'Gallery', href: '#gallery' },
                { label: t('map_subtitle'), href: '#location' },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="block text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground-950 uppercase tracking-wider mb-4">{t('footer_info')}</h4>
            <div className="space-y-3">
              {[
                { label: t('footer_privacy'), href: '#' },
                { label: t('footer_terms'), href: '#' },
                { label: t('footer_license'), href: '#' },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="block text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground-950 uppercase tracking-wider mb-4">{t('footer_contact')}</h4>
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="space-y-3"
            >
              <input
                type="text"
                name="name"
                placeholder={t('footer_name_placeholder')}
                required
                className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all duration-200"
              />
              <input
                type="email"
                name="email"
                placeholder={t('footer_email_placeholder')}
                required
                className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all duration-200"
              />
              <textarea
                name="message"
                placeholder={t('footer_message_placeholder')}
                rows={2}
                maxLength={500}
                required
                className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all duration-200 resize-none"
              ></textarea>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'sending'}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white px-4 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer"
              >
                {formStatus === 'sending' ? t('footer_sending') : t('footer_send')}
              </button>

              {formStatus === 'success' && (
                <p className="text-xs text-accent-600 text-center font-medium">
                  {t('footer_success')}
                </p>
              )}
              {formStatus === 'error' && (
                <p className="text-xs text-red-500 text-center font-medium">
                  {formError}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-background-200 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} Villas Yaiza. {t('footer_rights')}
          </p>
          <p className="text-xs text-foreground-400">
            Yaiza · Lanzarote · Islas Canarias
          </p>
        </div>
      </div>
    </footer>
  );
}